import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, ChevronLeft, ChevronRight, Newspaper } from "lucide-react";
import { getPressSource } from "@/data/presse-sources";
import { formatPressDate } from "@/lib/presse-format";
import type { PressContextPayload } from "@/lib/presse.functions";

/**
 * Passerelle vidéo → presse écrite : carrousel d'articles de presse liés au
 * sujet de la vidéo (même lieu ou même thème). Chaque carte donne un aperçu
 * rapide (média, date, extrait) et renvoie vers l'article original.
 */
export function PressBridge({ context }: { context: PressContextPayload | null }) {
  const trackRef = useRef<HTMLUListElement | null>(null);
  const [expanded, setExpanded] = useState(false);

  if (!context) return null;
  const splat = `${context.kind}/${context.slug}`;
  const scrollable = context.items.length > 1;

  const scrollByCard = (dir: -1 | 1) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector("li");
    const step = card ? card.getBoundingClientRect().width + 16 : 320;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      aria-label="Articles de presse pour approfondir le sujet"
      className="mt-12 overflow-hidden rounded-2xl border-2 border-foreground"
    >
      {/* Bandeau d'en-tête inversé, fortement contrasté */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-foreground px-6 py-4 text-background">
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-full border border-background/40">
            <Newspaper className="size-5" aria-hidden />
          </span>
          <div>
            <p className="text-[0.6rem] uppercase tracking-[0.3em] opacity-70">
              Pour approfondir le sujet
            </p>
            <h2 className="text-lg font-semibold leading-tight sm:text-xl">
              Ce que la presse a écrit sur {context.label}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="rounded-full border border-background/40 px-3 py-1 text-xs font-medium">
            {context.total} article{context.total > 1 ? "s" : ""} référencé
            {context.total > 1 ? "s" : ""}
          </span>
          {scrollable && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => scrollByCard(-1)}
                aria-label="Articles précédents"
                className="grid size-9 place-items-center rounded-full border border-background/40 transition-colors hover:bg-background hover:text-foreground"
              >
                <ChevronLeft className="size-5" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => scrollByCard(1)}
                aria-label="Articles suivants"
                className="grid size-9 place-items-center rounded-full border border-background/40 transition-colors hover:bg-background hover:text-foreground"
              >
                <ChevronRight className="size-5" aria-hidden />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-card p-6">
        <p className="text-sm text-muted-foreground">
          Complétez cette vidéo par ce qu'écrivait la presse écrite sur le même
          sujet — faites défiler, puis ouvrez l'article original en accès
          libre.
        </p>

        {/* Carrousel déroulant : cartes avec média, date et extrait */}
        <ul
          ref={trackRef}
          className="mt-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-label="Carrousel des articles de presse"
        >
          {(expanded ? context.items : context.items.slice(0, 3)).map((a, i) => {
            const source = getPressSource(a.source);
            return (
              <li
                key={`${a.source}-${a.id}`}
                className="w-[85%] shrink-0 snap-start sm:w-[320px]"
              >
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex h-full flex-col rounded-xl border border-border bg-background p-5 transition-colors hover:border-foreground"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="rounded-full bg-foreground px-2.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-background">
                      {source?.name ?? a.source}
                    </span>
                    <span className="text-[0.6rem] tabular-nums text-muted-foreground">
                      {i + 1}/{context.items.length}
                    </span>
                  </div>
                  <time
                    dateTime={a.date}
                    className="mt-2 text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground"
                  >
                    {formatPressDate(a.date)}
                  </time>
                  <p className="mt-2 line-clamp-3 text-sm font-semibold leading-snug group-hover:underline">
                    {a.title}
                  </p>
                  <p className="mt-2 line-clamp-3 text-xs text-muted-foreground">
                    {a.excerpt}
                  </p>
                  <span className="mt-auto flex items-center gap-1.5 pt-4 text-xs font-medium text-muted-foreground transition-colors group-hover:text-foreground">
                    Lire l'article
                    <ArrowUpRight
                      className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      aria-hidden
                    />
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
        {context.items.length > 3 && <Button variant="outline" className="mt-4" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>{expanded ? "Voir moins" : "Voir plus"}</Button>}

        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/presse/$"
            params={{ _splat: splat }}
            className="rounded-full bg-brand px-5 py-2 text-sm font-medium text-primary-foreground transition-transform hover:scale-[1.03]"
          >
            Toute la presse sur {context.label}
          </Link>
          {context.others.map((o) => (
            <Link
              key={o.slug}
              to="/presse/$"
              params={{ _splat: `${context.kind}/${o.slug}` }}
              className="rounded-full border border-border px-4 py-2 text-sm hover:bg-accent"
            >
              {o.label} ({o.total})
            </Link>
          ))}
          <Link
            to="/presse"
            className="rounded-full border border-dashed border-border px-4 py-2 text-sm hover:bg-accent"
          >
            Toute l'archive de presse
          </Link>
        </div>
      </div>
    </section>
  );
}
